/**
 * File Upload React Query Hooks
 *
 * This module provides React Query hooks for file upload operations including:
 * - Requesting upload URLs from the backend
 * - Confirming upload completion to the backend
 *
 * All hooks follow React Query v5 patterns with proper caching, error handling,
 * and state management integration.
 *
 * Note: For fetching deliverable sets, import `useDeliverableSetList` directly
 * from '@/features/deliverable-set/hooks' to follow DRY principle.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEY_DELIVERABLE_SET_LIST } from '@/features/deliverable-set/constants'
import { RequestBatchUploadConfirm, RequestUploadUrls } from '@/schema'

import { fileUploadApi } from './services'

export const useRequestUploadUrls = () => {
  return useMutation({
    mutationFn: (data: RequestUploadUrls) =>
      fileUploadApi.requestUploadUrls(data),
    onError: (error) => {
      throw error
    },
  })
}

export const useConfirmUpload = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RequestBatchUploadConfirm) =>
      fileUploadApi.confirmUpload(data),
    onSuccess: (_data, variables) => {
      // Invalidate deliverable set list to update file counts
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_DELIVERABLE_SET_LIST],
        refetchType: 'active',
      })

      // Invalidate document list to show newly uploaded files
      queryClient.invalidateQueries({
        queryKey: ['document-set-list'],
        refetchType: 'active',
      })

      // Invalidate version list for each uploaded document
      // This ensures the document viewer shows the latest versions after upload
      const documentIds = new Set(
        variables.confirmations.map((c) => c.document_id)
      )
      documentIds.forEach((documentId) => {
        queryClient.invalidateQueries({
          queryKey: ['version-list', documentId],
          refetchType: 'active',
        })
      })
    },
    onError: (error) => {
      throw error
    },
  })
}
